import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import {
  Package,
  TrendingDown,
  RotateCcw,
  AlertTriangle,
  Calendar,
  Search,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Filter,
  ShoppingCart,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n, dec = 0) =>
  n == null ? "—" : n.toLocaleString("es-ES", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtEur = (n) =>
  n == null ? "—" : n.toLocaleString("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });

function KpiCard({ icon: Icon, label, value, sub, color = "text-slate-700", iconBg = "bg-slate-100", testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SortIcon({ field, current, order }) {
  if (current !== field) return <ChevronDown className="w-3 h-3 text-slate-300" />;
  return order === "asc"
    ? <ChevronUp className="w-3 h-3 text-red-500" />
    : <ChevronDown className="w-3 h-3 text-red-500" />;
}

const PLATFORM_LABEL = { prestashop: "PrestaShop", shopify: "Shopify" };

function StatusBadge({ days, isNew, daysInCatalog }) {
  if (isNew) return <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs font-medium" title={`En catálogo hace ${daysInCatalog ?? "—"}d, sin ventas aún`}>Nuevo</Badge>;
  if (days >= 180) return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs font-medium">Muerto</Badge>;
  if (days >= 90)  return <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs font-medium">Estancado</Badge>;
  if (days >= 45)  return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 text-xs font-medium">Lento</Badge>;
  return                  <Badge className="bg-green-100 text-green-700 border-green-200 text-xs font-medium">Activo</Badge>;
}

function classifyStatus(p) {
  if (p.is_new) return "new";
  const d = p.days_no_sale;
  if (d >= 180) return "dead";
  if (d >= 90) return "stagnant";
  if (d >= 45) return "slow";
  return "active";
}

const STATUS_OPTIONS = [
  { value: "all", label: "Todos los estados" },
  { value: "active", label: "Activo" },
  { value: "slow", label: "Lento" },
  { value: "stagnant", label: "Estancado" },
  { value: "dead", label: "Muerto" },
  { value: "new", label: "Nuevo" },
];

const MONTH_NAMES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
function formatPeriod(period) {
  if (!period) return "—";
  const [y, m] = period.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

export default function AnalyticsStockPage() {
  const token = localStorage.getItem("auth_token");

  const [kpis, setKpis] = useState(null);
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshingStock, setRefreshingStock] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  const [search, setSearch] = useState("");
  const [filterStagnant, setFilterStagnant] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");  // all | active | slow | stagnant | dead | new
  const [sortBy, setSortBy] = useState("stock_value");
  const [sortOrder, setSortOrder] = useState("desc");
  const [fixingPacks, setFixingPacks] = useState(false);
  const [fixPackResult, setFixPackResult] = useState(null);

  const handleFixPacks = async () => {
    if (!token) return;
    setFixingPacks(true);
    setFixPackResult(null);
    try {
      const res = await axios.post(`${API}/api/prestashop/fix-pack-flags`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFixPackResult({ ok: true, msg: res.data.message || "Packs marcados correctamente" });
      fetchData();
    } catch (e) {
      setFixPackResult({ ok: false, msg: e.response?.data?.detail || "Error al marcar packs" });
    } finally {
      setFixingPacks(false);
    }
  };

  const handleRefreshStock = async () => {
    if (!token) return;
    setRefreshingStock(true);
    setRefreshResult(null);
    try {
      const res = await axios.post(`${API}/api/analytics/stock/refresh-stock`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRefreshResult({ ok: true, msg: res.data.message });
      fetchData();
    } catch (e) {
      setRefreshResult({ ok: false, msg: e.response?.data?.detail || "Error al actualizar stock" });
    } finally {
      setRefreshingStock(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = { sort_by: sortBy, sort_order: sortOrder, filter_stagnant: filterStagnant };
      if (search) params.search = search;

      const [stockRes, histRes] = await Promise.all([
        axios.get(`${API}/api/analytics/stock`, { headers, params }),
        axios.get(`${API}/api/analytics/stock/history`, { headers }),
      ]);
      setKpis(stockRes.data.kpis);
      setProducts(stockRes.data.products || []);
      setHistory(histRes.data.history || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al cargar datos de stock");
    } finally {
      setLoading(false);
    }
  }, [token, sortBy, sortOrder, filterStagnant, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Client-side status filter (server already handles search & stagnant flag)
  const visibleProducts = filterStatus === "all"
    ? products
    : products.filter(p => classifyStatus(p) === filterStatus);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(o => o === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const ThCell = ({ field, children }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-slate-800 select-none whitespace-nowrap"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <SortIcon field={field} current={sortBy} order={sortOrder} />
      </span>
    </th>
  );

  return (
    <DashboardLayout>
    <div className="p-6 space-y-6" data-testid="stock-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Análisis de Stock</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Valor de inventario, rotación y capital inmovilizado en tiempo real
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleFixPacks}
            disabled={fixingPacks}
            data-testid="fix-packs-btn"
            title="Excluir productos Pack de PrestaShop del stock (sin necesidad de sync completo)"
            className="flex items-center gap-2 px-3 py-2 text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
          >
            <Package className={`w-3.5 h-3.5 ${fixingPacks ? "animate-pulse" : ""}`} />
            {fixingPacks ? "Marcando packs..." : "Excluir Packs PS"}
          </button>
          <button
            onClick={handleRefreshStock}
            disabled={refreshingStock}
            data-testid="refresh-stock-quantities-btn"
            title="Actualiza las cantidades de stock directamente desde PrestaShop para todos los productos con stock > 0"
            className="flex items-center gap-2 px-3 py-2 text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${refreshingStock ? "animate-spin" : ""}`} />
            {refreshingStock ? "Actualizando stock..." : "Actualizar Stock PS"}
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="stock-refresh-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Cargando..." : "Actualizar"}
          </button>
        </div>
      </div>

      {fixPackResult && (
        <div className={`rounded-lg p-3 text-sm ${fixPackResult.ok ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
          {fixPackResult.msg}
        </div>
      )}

      {refreshResult && (
        <div className={`rounded-lg p-3 text-sm ${refreshResult.ok ? "bg-blue-50 border border-blue-200 text-blue-700" : "bg-red-50 border border-red-200 text-red-700"}`} data-testid="refresh-stock-result">
          {refreshResult.msg}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700" data-testid="stock-error">
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard
          icon={Package}
          label="Importe Total Stock"
          value={kpis ? fmtEur(kpis.total_stock_value) : "—"}
          sub={kpis ? `${fmt(kpis.total_products)} productos` : ""}
          color="text-slate-800"
          iconBg="bg-slate-100"
          testId="kpi-total-stock"
        />
        <KpiCard
          icon={RotateCcw}
          label="Rotación Anual"
          value={kpis ? `${fmt(kpis.rotation, 1)}x` : "—"}
          sub="COGS 12m / valor stock"
          color="text-blue-700"
          iconBg="bg-blue-50"
          testId="kpi-rotation"
        />
        <KpiCard
          icon={Calendar}
          label="Días de Cobertura"
          value={kpis?.coverage_days ? `${fmt(kpis.coverage_days)} días` : "—"}
          sub="Al ritmo de ventas actual"
          color="text-violet-700"
          iconBg="bg-violet-50"
          testId="kpi-coverage"
        />
        <KpiCard
          icon={ShoppingCart}
          label="Líneas/Mes (media)"
          value={kpis ? fmt(kpis.monthly_lines_avg) : "—"}
          sub="Líneas de pedido últimos 12m"
          color="text-emerald-700"
          iconBg="bg-emerald-50"
          testId="kpi-monthly-lines"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Stock Estancado"
          value={kpis ? `${fmt(kpis.stagnant_pct, 1)}%` : "—"}
          sub={kpis ? `${fmt(kpis.stagnant_count)} SKUs sin vender +${kpis.stagnant_days_threshold}d` : ""}
          color="text-orange-600"
          iconBg="bg-orange-50"
          testId="kpi-stagnant-pct"
        />
        <KpiCard
          icon={TrendingDown}
          label="Capital Inmovilizado"
          value={kpis ? fmtEur(kpis.stagnant_value) : "—"}
          sub="Stock sin venta en +90 días"
          color="text-red-600"
          iconBg="bg-red-50"
          testId="kpi-stagnant-value"
        />
      </div>

      {/* Monthly order lines chart */}
      {kpis?.monthly_lines_data?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Líneas de pedido vendidas por mes</h2>
              <p className="text-xs text-slate-400 mt-0.5">Cada línea = 1 producto en 1 pedido (independiente de unidades)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={kpis.monthly_lines_data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                tickFormatter={(v) => { const [,m] = v.split("-"); return ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"][parseInt(m,10)-1]; }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 10, fill: "#cbd5e1" }} width={45} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(v) => [v.toLocaleString("es-ES"), "Líneas"]}
                labelFormatter={(v) => { const [y,m] = v.split("-"); return `${["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"][parseInt(m,10)-1]} ${y}`; }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
              />
              <Bar dataKey="lines" radius={[4, 4, 0, 0]} maxBarSize={44} fill="#C8192C" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar producto, referencia o marca..."
            className="pl-9"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="stock-search-input"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          data-testid="stock-filter-status"
          className="px-4 py-2 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-300"
        >
          {STATUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          onClick={() => setFilterStagnant(f => !f)}
          data-testid="stock-filter-stagnant"
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border transition-colors ${
            filterStagnant
              ? "bg-orange-50 border-orange-300 text-orange-700 font-medium"
              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Filter className="w-4 h-4" />
          Solo estancados
        </button>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="stock-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <ThCell field="name">Producto</ThCell>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Marca</th>
                <ThCell field="quantity">Uds.</ThCell>
                <ThCell field="unit_cost">Coste/ud</ThCell>
                <ThCell field="stock_value">Valor Stock</ThCell>
                <ThCell field="units_12m">Vendidas 12m</ThCell>
                <ThCell field="days_no_sale">Días sin vender</ThCell>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && visibleProducts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Cargando stock desde PrestaShop y Shopify...
                  </td>
                </tr>
              ) : visibleProducts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                    {filterStatus !== "all" || filterStagnant || search
                      ? "No hay productos que coincidan con los filtros."
                      : "No hay datos de stock disponibles."}
                    <br />
                    <span className="text-xs">Asegúrate de tener PrestaShop o Shopify sincronizados con productos y precios de coste.</span>
                  </td>
                </tr>
              ) : (
                visibleProducts.map((p, i) => (
                  <tr
                    key={p.product_uid}
                    className={`hover:bg-slate-50 transition-colors ${p.is_stagnant ? "bg-orange-50/30" : ""}`}
                    data-testid={`stock-row-${i}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800 truncate max-w-[260px]" title={p.name}>{p.name}</div>
                      {p.reference && <div className="text-xs text-slate-400">{p.reference}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs truncate max-w-[140px]" title={p.brand || ""}>{p.brand || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-700 tabular-nums">{fmt(p.quantity)}</td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{fmtEur(p.unit_cost)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800 tabular-nums">{fmtEur(p.stock_value)}</td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{fmt(p.units_12m, 0)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className={p.is_new ? "text-blue-600 font-semibold" : (p.days_no_sale >= 90 ? "text-orange-600 font-semibold" : "text-slate-600")}>
                        {p.is_new ? `Nuevo (${fmt(p.days_in_catalog)}d)` : (p.days_no_sale >= 999 ? "Nunca" : `${fmt(p.days_no_sale)}d`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge days={p.days_no_sale} isNew={p.is_new} daysInCatalog={p.days_in_catalog} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {visibleProducts.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400 flex items-center justify-between">
            <span>
              {filterStatus === "all" && !filterStagnant && !search
                ? `${fmt(products.length)} productos`
                : `${fmt(visibleProducts.length)} de ${fmt(products.length)} productos`}
            </span>
            <span>Datos actualizados cada hora desde PrestaShop / Shopify</span>
          </div>
        )}
      </div>

      {/* Monthly History */}
      <div data-testid="stock-history-section">
        <h2 className="text-lg font-semibold text-slate-800 mb-3">Histórico mensual de stock</h2>
        {history.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
            Aún no hay snapshots mensuales. Se generarán automáticamente el último día de cada mes.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Mes</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Valor Total</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Capital Inmovilizado</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">% Estancado</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Rotación</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Días Cobertura</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Productos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((h, i) => (
                    <tr key={h.period} className="hover:bg-slate-50" data-testid={`history-row-${i}`}>
                      <td className="px-4 py-3 font-medium text-slate-700">{formatPeriod(h.period)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800 tabular-nums">{fmtEur(h.total_stock_value)}</td>
                      <td className="px-4 py-3 text-right text-orange-600 font-medium tabular-nums">{fmtEur(h.stagnant_value)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className={h.stagnant_pct > 30 ? "text-red-600 font-semibold" : "text-slate-600"}>
                          {fmt(h.stagnant_pct, 1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-blue-700 tabular-nums">{fmt(h.rotation, 1)}x</td>
                      <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{h.coverage_days ? `${fmt(h.coverage_days)}d` : "—"}</td>
                      <td className="px-4 py-3 text-right text-slate-500 tabular-nums">{fmt(h.total_products)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
    </DashboardLayout>
  );
}
